/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ButtonHTMLAttributes, memo, useCallback, useEffect, useMemo, useState} from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import NightsStayOutlined from '@material-ui/icons/NightsStayOutlined';
import WbSunnyOutlined from '@material-ui/icons/WbSunnyOutlined';
import {translate} from '@ion/core/src/locale/locale';
import {logThemeModeToggleClick} from './DLSThemeLogger';
import {isCurrentThemeActive, setThemeMode} from './ThemeStore';
import {DLSThemeModes} from './types';
import {useThemeMode} from './useThemeContext';

/**
 * Defines the places the toggle is rendered in, each with its own markup:
 * `nav` is an icon within the side navigation, with a tooltip; `header` is
 * a button within the mobile header.
 */
export type ThemeModeToggleSlot = 'nav' | 'header';

/**
 * Defines the class names a slot applies to the toggle's own elements.
 */
export interface ThemeModeToggleClasses {
  /** The element which is clicked. */
  root?: string;
  /** The frame around the icon, in the `header` slot. */
  icon?: string;
  /** The tooltip's popper, in the `nav` slot. */
  popper?: string;
  /** The tooltip, in the `nav` slot. */
  tooltip?: string;
  /** The text within the tooltip, in the `nav` slot. */
  tooltipText?: string;
}

export interface ThemeModeToggleProps {
  /** Whether the user may toggle; the caller's own checks of access. */
  canToggle: boolean;
  slot: ThemeModeToggleSlot;
  classes?: ThemeModeToggleClasses;
  /** Further attributes of the `header` slot's button, e.g. press handlers. */
  buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

/**
 * Defines the results of the `useThemeModeToggle` hook.
 */
export interface UseThemeModeToggleResults {
  /** `true` while the toggle is to be rendered. */
  available: boolean;
  /** The translated label of the switch the toggle performs. */
  label: string;
  /** The icon of the mode the toggle switches to. */
  Icon: typeof WbSunnyOutlined;
  /** Logs the switch, then switches to the other theme mode. */
  onToggle: () => void;
}

/**
 * Defines the props both slot components of the toggle receive.
 */
type ThemeModeToggleSlotProps = Pick<UseThemeModeToggleResults, 'label' | 'Icon' | 'onToggle'> & {
  classes: ThemeModeToggleClasses;
};

/**
 * Provides the class names applied while the caller passes none. A single
 * static reference is used, so the props of the slot components stay stable.
 */
const NO_CLASSES: ThemeModeToggleClasses = {};

/**
 * @private
 *
 * Tracks whether the current theme is active; i.e. multi-theme switching is
 * enabled and the current theme is selected. It subscribes to the
 * `isCurrentThemeActive` computed of the theme store, so the calling
 * component re-renders only when that answer changes.
 */
const useCurrentThemeActive = (): boolean => {
  const [active, setActive] = useState(isCurrentThemeActive?.value);

  useEffect(() => {
    const unsubscribe = isCurrentThemeActive.subscribe(setActive);
    return () => {
      unsubscribe();
    };
  }, []);

  return active;
};

/**
 * Provides the state and the action behind the theme mode toggle, which both
 * of its slots share: whether it is available, its label and icon, and the
 * action which switches the theme mode. The toggle is available while the
 * caller allows it (`canToggle`), the current theme is active and multiple
 * themes are enabled. The calling component re-renders when the effective
 * theme mode changes, and when the current theme becomes active or inactive.
 *
 * ```typescript
 *
 *   import {useThemeModeToggle} from '@ion/dls/src/theme/ThemeModeToggle';
 *   ...
 *
 *   const {available, label, Icon, onToggle} = useThemeModeToggle(canToggle);
 *
 * ```
 *
 * @param canToggle The caller's own check of access.
 * @returns         Whether the toggle is available, its label and icon, and
 *                  the action which switches the theme mode.
 */
export const useThemeModeToggle = (canToggle: boolean): UseThemeModeToggleResults => {
  const {isDark, mode} = useThemeMode();
  const isActive       = useCurrentThemeActive();

  const label = translate(isDark ? 'common:theme.switchToLight' : 'common:theme.switchToDark');
  const Icon  = isDark ? WbSunnyOutlined : NightsStayOutlined;

  const onToggle = useCallback(function() {
    const next = isDark ? DLSThemeModes.light : DLSThemeModes.dark;
    logThemeModeToggleClick(mode, next);
    setThemeMode(next);
  }, [mode, isDark]);

  return {
    available : canToggle && isActive,
    Icon,
    label,
    onToggle
  };
};

/**
 * @private
 *
 * Renders the toggle as an icon within the side navigation, with a tooltip
 * which also responds to the keyboard.
 */
const NavThemeModeToggle = ({classes, Icon, label, onToggle}: ThemeModeToggleSlotProps) => {
  const {popper, root, tooltip, tooltipText} = classes;

  const tooltipClasses = useMemo(() => ({popper, tooltip}), [popper, tooltip]);

  const onKeyDown = useCallback(function(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  }, [onToggle]);

  return (
    <Tooltip title={(
      <span className={tooltipText}>
        {label}
      </span>
    )}
    placement='right'
    enterDelay={0}
    leaveDelay={0}
    classes={tooltipClasses}>
      <div aria-label={label}
        className={root}
        onClick={onToggle}
        onKeyDown={onKeyDown}
        role='button'
        tabIndex={0}>
        <Icon fontSize='small' />
      </div>
    </Tooltip>
  );
};

/**
 * @private
 *
 * Renders the toggle as a button within the mobile header.
 */
const HeaderThemeModeToggle = ({
  buttonProps,
  classes,
  Icon,
  label,
  onToggle
}: ThemeModeToggleSlotProps & Pick<ThemeModeToggleProps, 'buttonProps'>) => (
  <button {...buttonProps}
    type='button'
    aria-label={label}
    className={classes.root}
    onClick={onToggle}>
    <span className={classes.icon}>
      <Icon />
    </span>
  </button>
);

/**
 * Displays the Sun/Moon toggle which switches the current theme between its
 * light and dark modes - a Moon in light mode, to switch to dark, and a Sun
 * in dark mode, to switch to light. It renders only while the caller's own
 * checks allow (`canToggle`), the current theme is active and multiple themes
 * are enabled; otherwise it renders nothing. Re-renders when the mode changes.
 *
 * The default export is memoized, so a caller which passes stable `classes`
 * and `buttonProps` does not re-render it with its own renders.
 *
 * ```typescript
 *
 *   import ThemeModeToggle from '@ion/dls/src/theme/ThemeModeToggle';
 *   ...
 *
 *   <ThemeModeToggle canToggle={hasAccess && !isResellerHost()}
 *     slot='nav'
 *     classes={classes} />
 *
 * ```
 *
 * @param props The caller's check of access, the slot the toggle is rendered
 *              in, and the class names the slot applies to it.
 * @returns The toggle, or nothing while it is not available.
 */
export const ThemeModeToggle = ({
  canToggle,
  slot,
  buttonProps,
  classes = NO_CLASSES
}: ThemeModeToggleProps) => {
  const {available, Icon, label, onToggle} = useThemeModeToggle(canToggle);

  let toggle = null;
  if (available) {
    toggle = slot === 'nav' ? (
      <NavThemeModeToggle classes={classes}
        Icon={Icon}
        label={label}
        onToggle={onToggle} />
    ) : (
      <HeaderThemeModeToggle buttonProps={buttonProps}
        classes={classes}
        Icon={Icon}
        label={label}
        onToggle={onToggle} />
    );
  }

  return toggle;
};

export default memo(ThemeModeToggle);
